<script>
import { mapGetters } from 'vuex';
import { useReportMetrics } from 'dashboard/composables/useReportMetrics';
import { GROUP_BY_FILTER, METRIC_CHART } from './constants';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import { formatTime } from '@chatwoot/utils';
import ChartStats from './components/ChartElements/ChartStats.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import SummaryChart from './components/SummaryChart.vue';
import LineChart from 'shared/components/charts/LineChart.vue';

export default {
  components: { ChartStats, BarChart, SummaryChart, LineChart },
  props: {
    groupBy: {
      type: Object,
      default: () => ({}),
    },
    accountSummaryKey: {
      type: String,
      default: 'getAccountSummary',
    },
    summaryFetchingKey: {
      type: String,
      default: 'getAccountSummaryFetchingStatus',
    },
    reportKeys: {
      type: Object,
      default: () => ({
        CONVERSATIONS: 'conversations_count',
        INCOMING_MESSAGES: 'incoming_messages_count',
        OUTGOING_MESSAGES: 'outgoing_messages_count',
        FIRST_RESPONSE_TIME: 'avg_first_response_time',
        RESOLUTION_TIME: 'avg_resolution_time',
        RESOLUTION_COUNT: 'resolutions_count',
        REPLY_TIME: 'reply_time',
      }),
    },
    datasetType: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const { calculateTrend, isAverageMetricType } = useReportMetrics(
      props.accountSummaryKey
    );
    return { calculateTrend, isAverageMetricType };
  },
  data() {
    return {
      chartRefs: {},
    };
  },
  computed: {
    ...mapGetters({
      accountReport: 'getAccountReports',
    }),
    metrics() {
      const reportKeys = Object.keys(this.reportKeys);
      const infoText = {
        FIRST_RESPONSE_TIME: this.$t(
          `REPORT.METRICS.FIRST_RESPONSE_TIME.INFO_TEXT`
        ),
        RESOLUTION_TIME: this.$t(`REPORT.METRICS.RESOLUTION_TIME.INFO_TEXT`),
      };
      return reportKeys.map(key => ({
        NAME: this.$t(`REPORT.METRICS.${key}.NAME`),
        KEY: this.reportKeys[key],
        DESC: this.$t(`REPORT.METRICS.${key}.DESC`),
        INFO_TEXT: infoText[key],
        TOOLTIP_TEXT: `REPORT.METRICS.${key}.TOOLTIP_TEXT`,
        trend: this.calculateTrend(this.reportKeys[key]),
      }));
    },
    getDataType() {
      return this.datasetType;
    },
  },
  methods: {
    getCollection(metric) {
      if (!this.accountReport.data[metric.KEY]) {
        return {};
      }
      const data = this.accountReport.data[metric.KEY];
      const labels = data.map(element => {
        if (this.groupBy?.period === GROUP_BY_FILTER[2].period) {
          let week_date = new Date(fromUnixTime(element.timestamp));
          const first_day = week_date.getDate() - week_date.getDay();
          const last_day = first_day + 6;
          const week_first_date = new Date(week_date.setDate(first_day));
          const week_last_date = new Date(week_date.setDate(last_day));
          return `${format(week_first_date, 'dd-MMM')} - ${format(
            week_last_date,
            'dd-MMM'
          )}`;
        }
        if (this.groupBy?.period === GROUP_BY_FILTER[3].period) {
          return format(fromUnixTime(element.timestamp), 'MMM-yyyy');
        }
        if (this.groupBy?.period === GROUP_BY_FILTER[4].period) {
          return format(fromUnixTime(element.timestamp), 'yyyy');
        }
        return format(fromUnixTime(element.timestamp), 'dd-MMM');
      });
      const datasetType = this.getDataType;
      const datasets = METRIC_CHART[metric.KEY].datasets.map(dataset => {
        let datasetTypeConfig = dataset.type;
        if (datasetType !== null) {
          datasetTypeConfig = datasetType;
        }
        switch (datasetTypeConfig) {
          case 'bar':
            return {
              ...dataset,
              yAxisID: 'y',
              label: metric.NAME,
              backgroundColor: this.getChartColorsFromCSS,
              data: data.map(element => element.value),
            };
          case 'line':
            return {
              ...dataset,
              yAxisID: 'y',
              label: this.metrics[0].NAME,
              backgroundColor: this.getChartColorsFromCSS,
              data: data.map(element => element.count),
            };
          default:
            return dataset;
        }
      });
      return {
        labels,
        datasets,
      };
    },
    getChartOptions(metric) {
      const options = {
        scales: METRIC_CHART[metric.KEY].scales,
      };

      // Only add tooltip configuration for time-based metrics
      if (this.isAverageMetricType(metric.KEY)) {
        options.plugins = {
          tooltip: {
            callbacks: {
              label: ({ raw, dataIndex }) => {
                return this.$t(metric.TOOLTIP_TEXT, {
                  metricValue: formatTime(raw || 0),
                  conversationCount:
                    this.accountReport.data[metric.KEY][dataIndex]?.count || 0,
                });
              },
            },
          },
        };
      }

      return options;
    },
    getChartColorsFromCSS() {
      const theme =
        document.documentElement.style.getPropertyValue('color-scheme');

      if (theme === 'dark') return 'rgba(233, 85, 70, 1)';
      return 'rgba(177, 44, 104, 1)';
    },
    setChartRef(key, el) {
      if (el) this.chartRefs[key] = el;
    },
    scrollToChart(key) {
      const el = this.chartRefs[key];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
  },
};
</script>

<template>
  <div class="rounded-xl bg-n-background-component p-5">
    <span class="text-xl font-bold text-n-brand">
      {{ $t('AGENT_REPORTS.HEADER_SUMMARY') }}
    </span>
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-4">
      <div
        v-for="metric in metrics"
        :key="metric.KEY"
        :ref="el => setChartRef(metric.KEY, el)"
        class="p-4 rounded-xl bg-n-background-component-contrast"
      >
        <SummaryChart
          :metric="metric"
          :account-summary-key="accountSummaryKey"
          :summary-fetching-key="summaryFetchingKey"
          @scroll-to-chart="scrollToChart"
        />
      </div>
    </div>
  </div>
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 py-5 gap-8">
    <div
      v-for="metric in metrics"
      :key="metric.KEY"
      :ref="el => setChartRef(metric.KEY, el)"
      class="p-4 mb-3 rounded-xl bg-n-background-component hover:bg-n-brand chart-stat-root"
    >
      <ChartStats
        :metric="metric"
        :account-summary-key="accountSummaryKey"
        :summary-fetching-key="summaryFetchingKey"
      />
      <div class="mt-4 h-72">
        <woot-loading-state
          v-if="accountReport.isFetching[metric.KEY]"
          class="text-xs"
          :message="$t('REPORT.LOADING_CHART')"
        />
        <div
          v-else
          class="flex items-center justify-center h-72 bg-n-background-component-contrast rounded-md"
        >
          <template v-if="accountReport.data[metric.KEY].length">
            <template v-if="getDataType === 'line'">
              <LineChart
                :collection="getCollection(metric)"
                :chart-options="getChartOptions(metric)"
              />
            </template>
            <template v-else>
              <BarChart
                :collection="getCollection(metric)"
                :chart-options="getChartOptions(metric)"
              />
            </template>
          </template>
          <span v-else class="text-sm">
            {{ $t('REPORT.NO_ENOUGH_DATA') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.chart-stat-root:hover :deep(*) {
  color: white !important;
}
</style>
